/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// design-clip.js — copy/paste of a WHOLE DESIGN: the boards of a sub-assembly,
// everything seated on them, the desk bricks selected with them, and all the
// wiring between them — captured as one rigid arrangement and re-stamped
// somewhere else. That "somewhere else" is usually ANOTHER DESKTOP (a project
// tab, Feature 240): work a reference design out on one desktop, copy it,
// and drop it into the main build.
//
// This is `paste-cluster.js` one level up. That module carries loose PARTS and
// re-seats each on whatever board it lands over; this one carries the BOARDS
// too, so the design brings its own holes with it and its internal wiring
// survives the trip. The two are deliberately separate: a part cluster asks
// "does each member find a free hole?", while a design asks only "is there room
// on the desk?" — the wiring inside it is already known to be sound.
//
// The capture is BOARDS-FIRST and total. Given a set of board ids it takes:
//   · those boards (type, rotation, position, group, name/description),
//   · EVERY component seated on them — the whole board comes, not just what a
//     marquee happened to touch — save one whose lead reaches OFF them (a
//     resistor bent to a rail left behind, a lead floating over bare desk):
//     that one touches the outside, so it stays, by the wire rule below. Taken,
//     its lead would land wherever the paste put it — in mid-air, which no
//     placement allows (a paste refused after a green ghost), or in a hole of
//     whatever board lay there, a connection the design never had,
//   · the desk bricks (PSU / clock) explicitly named in the selection,
//   · every wire whose BOTH endpoints land on a captured owner (a wire with one
//     end outside would have to be cut, so it is simply not part of the design),
//   · and the metadata riding those wires and parts — buses whose members are
//     all captured, net names bound to captured addresses, and labels anchored
//     to captured parts.
// Everything is stored in the SOURCE's own world coordinates plus a `center`
// (the arrangement's bounding-box middle), so a paste is one integer-pitch
// translation away — exactly the paste-cluster contract, and for the same
// reason: the desk is an integer 0.1-in lattice, so a rigid integer shift maps
// holes onto holes and can never land a design half a hole off.
//
// Legality is per-BOARD and per-BRICK only, and the clip is ALL-OR-NOTHING.
// A rigid translation preserves the arrangement, so the parts and wires inside
// a captured design can only ever conflict with each other in ways they already
// didn't; the one thing that can go wrong is a board (or brick) landing on top
// of something already on the destination desk. And unlike a part cluster —
// where a red member is simply dropped from the paste — half a design is not a
// design: leaving a board behind would silently cut every wire that crossed to
// it. So `resolveDesign` reports one verdict for the whole clip.
//
// Pure and DOM-free: the controller renders the clip, the document stamps it.

import { partDef } from "../catalog/index.js";
import { dropRunLatches } from "../catalog/run-latches.js";
import { formatAddress, parseAddress } from "./breadboard.js";
import { boardRect, snapCorrection } from "./mating.js";
import { partPinAddresses } from "./occupancy.js";

/**
 * A copied part's params: a deep copy with everything RUN-VOLATILE or
 * per-instance stripped. The run latches are run state (a fresh part is never
 * pre-damaged — catalog/run-latches.js) and a memory chip's `storage`/`programmed` binding belongs to
 * the chip it was minted for — the paste mints its own file (the controller's
 * `#provisionMemory`), so a copy must never inherit the source's bytes.
 */
function freshParams(params) {
  const copy = params ? structuredClone(params) : {};
  dropRunLatches(copy); // 12 V's and Spice Lite's smoke: run state
  delete copy.storage;
  delete copy.programmed;
  return copy;
}

/** A desk brick's footprint rect, or null for a part with no declared size. */
function brickRect(comp) {
  const size = partDef(comp.ref)?.size;
  return size
    ? { x: comp.x, y: comp.y, width: size.width, height: size.height }
    : null;
}

/** Copy `name`/`description` across when present (omit-when-empty, as the
    document itself stores them). */
function withMeta(target, source) {
  if (source.name) target.name = source.name;
  if (source.description) target.description = source.description;
  return target;
}

/**
 * Capture a design from a document. `boardIds` drives everything — a clip with
 * no board is not a design (loose parts are `paste-cluster.js`'s job), so this
 * returns null. `componentIds` only ever adds DESK BRICKS: board parts come
 * with their board, whether or not the selection named them.
 *
 * @param {{boards:Array, components:Array, wires:Array, buses?:Array,
 *   netNames?:Array, annotations?:Array}} doc - the live document (plain).
 * @param {{boardIds?: string[], componentIds?: string[]}} selection
 * @returns {object|null} the clip, or null when there is nothing to copy.
 */
export function captureDesign(doc, { boardIds = [], componentIds = [] } = {}) {
  const wanted = new Set(boardIds);
  const boards = (doc.boards ?? []).filter((b) => wanted.has(b.id));
  if (boards.length === 0) return null;
  const named = new Set(componentIds);
  const components = doc.components ?? [];
  const bricks = components.filter((c) => c.board == null && named.has(c.id));
  // Seated on a captured board, with every lead in one of its holes.
  const onCaptured = (c) =>
    (partPinAddresses(doc, c) ?? []).every((p) =>
      wanted.has(parseAddress(p.address ?? "")?.boardId),
    );
  const parts = components.filter(
    (c) => c.board != null && wanted.has(c.board) && onCaptured(c),
  );

  // A wire, a name, or a bus only travels when EVERYTHING it touches does.
  const owners = new Set([...wanted, ...bricks.map((b) => b.id)]);
  const inside = (address) => {
    const parsed = parseAddress(address);
    return parsed && owners.has(parsed.boardId) ? parsed : null;
  };
  const wires = [];
  for (const w of doc.wires ?? []) {
    const from = inside(w.from);
    const to = inside(w.to);
    if (!from || !to) continue;
    const clipped = {
      key: w.id,
      from: { owner: from.boardId, point: from.hole },
      to: { owner: to.boardId, point: to.hole },
      color: w.color,
    };
    // A ROUTED wire's shape is part of the design — a sub-assembly whose wiring
    // was taken around a board must arrive routed the same way. The waypoints
    // travel in the CLIP's own coordinates (the capture origin), exactly as the
    // boards do, so the paste shift moves them with everything else.
    if (w.layout === "routed") {
      clipped.layout = "routed";
      if (w.points?.length) clipped.points = w.points.map((p) => ({ ...p }));
    }
    wires.push(withMeta(clipped, w));
  }
  const wireKeys = new Set(wires.map((w) => w.key));
  const buses = (doc.buses ?? [])
    .filter(
      (b) => b.members.length > 0 && b.members.every((m) => wireKeys.has(m)),
    ) // prettier-ignore
    .map((b) => ({
      name: b.name,
      color: b.color,
      members: [...b.members],
    }));
  const netNames = (doc.netNames ?? [])
    .map((n) => ({ parsed: inside(n.address), name: n.name }))
    .filter((n) => n.parsed)
    .map((n) => ({
      owner: n.parsed.boardId,
      point: n.parsed.hole,
      name: n.name,
    }));
  // Only ANCHORED labels ride along — a label pinned to a captured part is
  // part of that part's documentation. A free-floating one belongs to the desk
  // it was written on, not to the design.
  const partKeys = new Set(parts.map((c) => c.id));
  const annotations = (doc.annotations ?? [])
    .filter((a) => a.anchor && partKeys.has(a.anchor))
    .map((a) => structuredClone(a));

  // A SIGNAL travels only when its flag is planted on a captured board — the
  // wire rule. An UNPLACED signal stays behind: it is bench equipment sitting
  // on the rail, not part of the design on the boards, the same argument that
  // leaves an unanchored label behind.
  //
  // `color` is deliberately NOT captured. Colours are handed out per desk, in a
  // cycle (`nextSignalColor`), so the paste re-issues one from the
  // destination's own sequence — the next it would give any new signal.
  const signals = [];
  for (const sig of doc.signals ?? []) {
    const parsed = sig.flag ? inside(sig.flag.anchor) : null;
    if (!parsed) continue;
    signals.push(
      withMeta(
        {
          type: sig.type,
          rest: sig.rest,
          flag: {
            owner: parsed.boardId,
            point: parsed.hole,
            rot: sig.flag.rot ?? 0,
          },
        },
        sig,
      ),
    );
  }

  // The grab reference: the bounding box of everything with a FOOTPRINT (the
  // boards and bricks), so the design tracks the cursor from its middle the
  // way a kit ghost does.
  const rects = [
    ...boards.map(boardRect),
    ...bricks.map(brickRect).filter(Boolean),
  ];
  const center = {
    x: (Math.min(...rects.map((r) => r.x)) + Math.max(...rects.map((r) => r.x + r.width))) / 2, // prettier-ignore
    y: (Math.min(...rects.map((r) => r.y)) + Math.max(...rects.map((r) => r.y + r.height))) / 2, // prettier-ignore
  };
  return {
    center,
    boards: boards.map((b) =>
      withMeta(
        {
          key: b.id,
          type: b.type,
          x: b.x,
          y: b.y,
          rot: b.rot ?? 0,
          group: b.group ?? null,
          ...(b.cuts?.length ? { cuts: [...b.cuts] } : {}),
        },
        b,
      ),
    ),
    bricks: bricks.map((c) => ({
      key: c.id,
      kind: c.kind,
      ref: c.ref,
      x: c.x,
      y: c.y,
      params: freshParams(c.params),
    })),
    parts: parts.map((c) =>
      withMeta(
        {
          key: c.id,
          board: c.board,
          anchor: c.anchor,
          kind: c.kind,
          ref: c.ref,
          params: freshParams(c.params),
        },
        c,
      ),
    ),
    wires,
    buses,
    netNames,
    annotations,
    signals,
  };
}

/**
 * The integer-pitch shift that puts a clip's centre under a world point.
 * Fractional is fine going in — the rounding here is what keeps every hole on
 * the lattice.
 */
export function shiftFor(clip, world) {
  return {
    dx: Math.round(world.x - clip.center.x),
    dy: Math.round(world.y - clip.center.y),
  };
}

/**
 * Resolve a clip translated by `shift`: where every board and brick lands, and
 * whether the design as a whole may be dropped there. See the module note on
 * why the verdict is all-or-nothing and why only footprints are checked.
 *
 * @param {object} clip - from captureDesign
 * @param {{dx:number, dy:number}} shift - integer pitch translation
 * @param {object} tests
 * @param {(type:string, x:number, y:number, rot:number)=>boolean}
 *   tests.canPlaceBoard
 * @param {(ref:string, x:number, y:number)=>boolean} tests.canPlaceBrick
 * @returns {{boards:Array, bricks:Array, legal:boolean}}
 */
export function resolveDesign(clip, shift, { canPlaceBoard, canPlaceBrick }) {
  const boards = clip.boards.map((b) => {
    const x = b.x + shift.dx;
    const y = b.y + shift.dy;
    return { ...b, x, y, legal: canPlaceBoard(b.type, x, y, b.rot) };
  });
  // A brick lives in whole units (DeskDoc.addBrick rounds it there), so it is
  // judged — and drawn — where the paste will put it, not a fraction beside.
  const bricks = clip.bricks.map((b) => {
    const x = Math.round(b.x + shift.dx);
    const y = Math.round(b.y + shift.dy);
    return { ...b, x, y, legal: canPlaceBrick(b.ref, x, y) };
  });
  return {
    boards,
    bricks,
    legal:
      boards.every((b) => b.legal) &&
      bricks.every((b) => b.legal) &&
      clip.boards.length > 0,
  };
}

/**
 * The magnetic pull on a design about to be dropped: the extra (dx, dy) that
 * would land one of its boards flush against a board already on the desk it
 * can dovetail with. The same `snapCorrection` a board drag and a kit ghost
 * use, so a pasted design mates exactly like a placed one.
 *
 * @param {object} clip
 * @param {{dx:number, dy:number}} shift
 * @param {Array<{x,y,width,height}>} stationary - rects of the existing boards
 */
export function snapDesign(clip, shift, stationary) {
  if (clip.boards.length === 0) return { dx: 0, dy: 0 };
  const pull = snapCorrection(
    clip.boards.map((b) =>
      boardRect({ ...b, x: b.x + shift.dx, y: b.y + shift.dy }),
    ),
    stationary,
  );
  // A board is PLACED on the column lattice (DeskDoc.addBoard), so a pull
  // to a fractional x — flush beside a turned rail, 3.50 wide — is one the
  // paste cannot keep: the ghost would show it flush and the board land half
  // a pitch away. Only a whole-pitch pull across; y keeps its fraction.
  return { dx: Math.round(pull.dx), dy: pull.dy };
}

/**
 * A clip re-expressed as a stand-alone `{boards, components, wires}` at the
 * origin its capture recorded — the shape the geometry helpers
 * (`addressWorld`, `partPinsWorld`) already read, so the GHOST can lay itself
 * out with the same code the desk uses instead of a parallel copy of the
 * arithmetic. Board/brick keys stand in as ids, so a clip wire's endpoint
 * address resolves against it unchanged.
 */
export function clipScene(clip) {
  return {
    boards: clip.boards.map(({ key, type, x, y, rot }) => ({
      id: key,
      type,
      x,
      y,
      rot,
    })),
    components: [
      ...clip.bricks.map(({ key, kind, ref, x, y, params }) => ({
        id: key,
        kind,
        ref,
        x,
        y,
        params,
        board: null,
      })),
      ...clip.parts.map(({ key, board, anchor, kind, ref, params }) => ({
        id: key,
        kind,
        ref,
        board,
        anchor,
        params,
      })),
    ],
    wires: clip.wires.map((w) => ({
      id: w.key,
      from: formatAddress(w.from.owner, w.from.point),
      to: formatAddress(w.to.owner, w.to.point),
      color: w.color,
      // Carried so the GHOST draws a routed wire routed — the ghost is the
      // design, and a run that dodges a board must not straighten out for the
      // one moment the user is deciding where to drop it.
      ...(w.layout === "routed"
        ? { layout: "routed", points: (w.points ?? []).map((p) => ({ ...p })) }
        : null),
    })),
    // The ghost draws the flags it is about to plant, from the SAME polygon
    // the desk layer uses — the preview and the drop must not draw two
    // different shapes. No id yet (the paste mints one), so the scene names
    // them by index; the ghost only ever reads position and colour.
    signals: (clip.signals ?? []).map((sig, i) => ({
      id: `clip-sig${i + 1}`,
      anchor: formatAddress(sig.flag.owner, sig.flag.point),
      rot: sig.flag.rot ?? 0,
    })),
  };
}
